import { DownloadIcon, LanguagesIcon, PlayIcon, XIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import type { NextEpisode } from "./next-episode";

/**
 * Nakładka „następny odcinek" po zakończeniu materiału.
 *
 * Mieszka **wewnątrz kontenera kadru**, nie pod nim. To nie jest szczegół układu:
 * na pełny ekran wchodzi kontener, więc wszystko, co leży obok niego, przestaje
 * istnieć dokładnie w chwili, gdy jest najbardziej potrzebne — a odcinek najczęściej
 * kończy się właśnie na pełnym ekranie. Ten sam błąd popełniła pierwsza wersja
 * sterowania napisami i jest o nim komentarz w `index.tsx`.
 *
 * Nakładka pokazuje się **tylko wtedy, gdy następny odcinek da się wziąć**
 * (`pickNextEpisode`). Propozycja jest obietnicą złożoną bez pytania, w chwili gdy
 * widz nie sięga po mysz; obietnica prowadząca do ekranu błędu jest gorsza niż cisza.
 *
 * Odliczanie jest opcjonalne i sterowane ustawieniem konta. Bez niego nakładka jest
 * zaproszeniem, nie automatem: sama nie zabierze scenki po napisach końcowych.
 */
export interface NextEpisodeOverlayProps {
  next: NextEpisode;
  /** Sekundy do automatycznego przejścia albo `null`, gdy automatu nie ma. */
  secondsLeft: number | null;
  /** Ile sekund odliczanie miało na starcie — potrzebne do rysowania paska. */
  countdownFrom: number;
  /** Okładka serii; bez niej nakładka jest tylko przygaszonym kadrem. */
  coverUrl?: string | null;
  busy: boolean;
  onPlay: () => void;
  onDismiss: () => void;
}

export function NextEpisodeOverlay({
  next,
  secondsLeft,
  countdownFrom,
  coverUrl = null,
  busy,
  onPlay,
  onDismiss,
}: NextEpisodeOverlayProps) {
  /**
   * Pasek postępu odliczania zamiast samej liczby.
   *
   * Liczba mówi „ile", pasek mówi „ile zostało" jednym spojrzeniem z trzech metrów —
   * a to jest odległość, z której się na to patrzy.
   */
  const elapsed = secondsLeft === null ? 0 : countdownFrom - secondsLeft;
  const percent =
    countdownFrom === 0 ? 0 : Math.min(100, Math.max(0, (elapsed / countdownFrom) * 100));

  return (
    <div
      // `z-30`: nad sterowaniem i nad warstwą napisów, bo przykrywa cały kadr.
      // Przygaszenie zamiast pełnej czerni — pod nakładką zostaje ostatnia klatka.
      className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 bg-background/85 p-6 backdrop-blur-sm"
      /**
       * `aria-live`, nie `role="dialog"`.
       *
       * Nakładka pojawia się **sama**, nie przechwytuje fokusu i nie blokuje reszty
       * strony — czyli nie jest okienkiem dialogowym, choć wygląda jak ono. Ogłoszenie
       * uprzejme mówi czytnikowi „coś się zmieniło" bez przerywania tego, co czyta.
       */
      aria-live="polite"
      aria-label={`Następny odcinek ${next.number}`}
    >
      <div className="flex items-center gap-4">
        {coverUrl !== null && (
          <img
            src={coverUrl}
            alt=""
            className="hidden h-28 rounded-md border border-border object-cover shadow-lg sm:block"
          />
        )}
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold tracking-[0.2em] text-muted-foreground uppercase">
            Następny odcinek
          </p>
          <p className="text-3xl font-bold">Odcinek {next.number}</p>
          {/*
            Mówimy, co się stanie po naciśnięciu. „Odtwórz" przy materiale, którego
            nie ma na dysku, znaczyłoby kilka minut pobierania — a widz spodziewałby
            się natychmiastowego obrazu.
          */}
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            {next.ready ? (
              <>
                <PlayIcon className="size-4" />
                Gotowy do odtworzenia
              </>
            ) : (
              <>
                <DownloadIcon className="size-4" />
                Najpierw pobranie — potrwa kilka minut
              </>
            )}
          </p>
          {next.fallbackOnly && (
            <p className="flex items-center gap-2 text-sm text-warning">
              <LanguagesIcon className="size-4" />
              Dostępny tylko z angielskimi napisami
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button size="lg" variant="hero" onClick={onPlay} disabled={busy}>
          <PlayIcon />
          {busy
            ? "Zaczynam…"
            : secondsLeft !== null
              ? `Odtwórz teraz (${secondsLeft} s)`
              : next.ready
                ? "Odtwórz"
                : "Przygotuj i odtwórz"}
        </Button>
        <Button size="lg" variant="secondary" onClick={onDismiss} disabled={busy}>
          <XIcon />
          {secondsLeft !== null ? "Zostań tutaj" : "Zamknij"}
        </Button>
      </div>

      {secondsLeft !== null && (
        <div
          className="h-1 w-56 overflow-hidden rounded-full bg-muted"
          // Pasek jest ozdobą liczby, która stoi na przycisku — czytnik przeczyta ją,
          // a drugi opis tego samego byłby szumem.
          aria-hidden="true"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width] duration-1000 ease-linear"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}
